import React, { PureComponent } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormGroup from "@mui/material/FormGroup";
import { Add as AddIcon, RemoveCircle as RemoveIcon } from '@mui/icons-material';
import moment from 'moment';

import * as dates from '@simplybenefits/sb-dates';
import { TextInput, DateInput, Services, Button, Text, Modal, BubbleSelectBlock, Checkbox, Bubble, i18n, tokens, localizationMaps } from '@simplybenefits/sb-ui-library';
import { globalStyles } from '../../../theme/index.js';
import {
    addDependentToList,
    editDependentInList,
    removeDependentFromList
} from '../../../actions/signup';
import Tip from '../../../components/Tip.js';

/**
 * 
 * @returns 
 */
const primaryBenefitsNames = () => [
    i18n.t(tokens.dropdowns.categoryOptions.drugs),
    i18n.t(tokens.dropdowns.categoryOptions.dental),
    i18n.t(tokens.titles.majorMedical),
    i18n.t(tokens.dropdowns.categoryOptions.vision),
    i18n.t(tokens.titles.outOfCountryAndProvince),
    i18n.t(tokens.titles.paramedical)
];

const insuranceOptions = [
    'Simply Benefits',
    'Desjardins',
    'Great-West Life',
    'Green Shield',
    'Manulife',
    'Pacific Blue Cross',
    'RBC Insurance',
    'RWAM',
    'SSQ',
    'Sun Life',
    'The Co-operators',
    'Other'
];

class Dependents extends PureComponent {
    state = {
        dependents: [ ...this.props.dependents ] || [],
        showDependentDialog: false,
        options: Services.selectFieldOptions.dependentOptionsWithSpouse(),
        numberOfDependents: 0,
        hasNoDependents: true,
        hasAcceptedInEligibleDependentTerms: false,
        ineligibleDependents: [],
        showNoDependentsMessage: false
    };

    async componentDidMount() {
        const data = {
            numberOfDependents: this.state.dependents.length,
            hasNoDependents: this.state.dependents.length === 0
        };

        // if enrolment coverage type changes are blocked and they are set to DUO then automatically add their depenent
        if (this.props.userDetails.blockEnrolmentCoverageTypeChanges === 1 && this.state.dependents.length === 0) {
            const dependents = [];

            const dependent = {
                firstname: '',
                lastname: '',
                gender: '',
                birthdate: '',
                cohabDate: '',
                dateOfMarriage: '',
                relationship: '',
                email: '',
                employer: null,
                insuranceCarrier: null,
                dependentCertNumber: null,
                spouseCoverage: null,
                hasProvincialHealthCoverage: true,
                primaryBenefits: []
            };

            if (localizationMaps.toEnglish("coverageTypes", this.props.userDetails.coverageType).toUpperCase() === 'DUO') {
                dependents.push(dependent);
                await this.props.dispatch(addDependentToList(dependent));

                data.numberOfDependents = 1;
            } else if (localizationMaps.toEnglish("coverageTypes", this.props.userDetails.coverageType).toUpperCase() === 'FAMILY') {
                dependents.push(dependent);
                dependents.push(dependent);

                await this.props.dispatch(addDependentToList(dependent));
                await this.props.dispatch(addDependentToList(dependent));

                data.numberOfDependents = 2;
            }

            data.hasNoDependents = false;
            data.dependents = [ ...this.state.dependents, ...dependents ];
        }

        this.setState(data);
    }

    _onBlur = async (type, i) => {
        if ((type === 'cohabDate' || type === 'relationship') && this.state.dependents[i].cohabDate.length > 0) {
            await this._checkDependentEligibility();
        } else if (type === 'birthdate') {
            // Show a helper text with a date in english to help the user understand what date they entered
            const birthdateValue = this.state.dependents[i].birthdate;
            const birthdateError = await Services.validateInput('birthdate', birthdateValue, i18n.locale);
            const readableBirthdate = birthdateError ? '' : dates.getStartOfDay(birthdateValue, 'll');

            this.setState({
                [`birthdateError${ i }`]: birthdateError,
                [`birthdateHelper${ i }`]: readableBirthdate
            });
        }

        // update state with new form field value so it sticks when switching between views
        this.props.dispatch(editDependentInList(i, this.state.dependents[i]));
    };

    _handleInputChange = async (type, event, i, isValue = false) => {
        // make the saved state to false, since we have made edits
        this.props.setSavedState('dependents', false);

        if (type === 'insuranceCarrier' && event.target.value === 'Other') {
            event.target.value = '';
        }

        if (i !== null && i !== undefined) {
            await this.setState({
                dependents: this.state.dependents.map((dependent, index) => {
                    if (i === index) {
                        return {
                            ...dependent,
                            [type]: isValue ? event : event.target.value
                        };
                    }

                    return dependent;
                })
            });
        } else {
            await this.setState({
                [type]: isValue ? event : event.target.value,
                hasAcceptedInEligibleDependentTerms: false
            });
        }

        if (type === 'relationship' && this.state.dependents[i].cohabDate.length > 0) {
            await this._checkDependentEligibility();
        }
    };

    /**
     * 
     * @param {*} option 
     */
    _handleBubbleListSelector = (option, i) => {
        const selection = [ ...this.state.dependents[i].primaryBenefits ];
        const index = selection.findIndex((e) => e === option);

        if (index === -1) {
            selection.push(option);
        } else {
            selection.splice(index, 1);
        }

        this._handleInputChange('primaryBenefits', selection, i, true);
    };

    _changeSpouseCoverage = async (value, i) => {
        await this.setState({
            dependents: this.state.dependents.map((dependent, index) => {
                if (i === index) {
                    return {
                        ...dependent,
                        spouseCoverage: value,
                        employer: value ? dependent.employer : null,
                        insuranceCarrier: value ? dependent.insuranceCarrier : null,
                        dependentCertNumber: value ? dependent.dependentCertNumber : null
                    };
                }

                return dependent;
            })
        });
    };

    _toggleDependentDialog = (state) => {
        this.setState({
            showDependentDialog: state
        });
    };

    _getEligibilityDate = (cohabDate) => {
        if (cohabDate.length === 0) {
            const oneYearFromNow = new Date();
            oneYearFromNow.setFullYear(oneYearFromNow.getFullYear() + 1);
            return dates.getStartOfDay(oneYearFromNow);
        }

        const oneYearFromCohabDate = new Date(cohabDate);
        oneYearFromCohabDate.setFullYear(oneYearFromCohabDate.getFullYear() + 1);
        return dates.getStartOfDay(oneYearFromCohabDate);
    };

    _isDependentEligible = (cohabDate, eligibilityDate) => {
        const now = dates.getUTC();
        return eligibilityDate < now;
    };

    _checkDependentEligibility = async () => {
        // if this is a marketplace only enrolment we don't care if the dependents are eligible
        if (this.props.isMarketplaceOnly) {
            return;
        }

        const ineligibleDependents = [];

        const promises = this.state.dependents.map((dependent) => {
            // if the dependent is not Common Law then they are eligible and do not require a reminder
            if (localizationMaps.toEnglish("relationships", dependent.relationship).toUpperCase() !== 'COMMON LAW') {
                return {
                    ...dependent,
                    requiresReminder: false
                };
            }

            const eligibilityDate = this._getEligibilityDate(dependent.cohabDate);
            const isEligible = this._isDependentEligible(dependent.cohabDate, eligibilityDate);

            if (isEligible) {
                return {
                    ...dependent,
                    requiresReminder: false,
                    eligibilityDate
                };
            }

            ineligibleDependents.push(dependent);

            return {
                ...dependent,
                eligibilityDate,
                requiresReminder: true
            };
        });

        const dependents = await Promise.all(promises);

        this.setState({
            dependents,
            ineligibleDependents
        });
    };

    _validateForm = async () => {
        let isValid = true;
        let seenSpouse = false;

        const promises = this.state.dependents.map(async (dependent, i) => {
            const { firstname, lastname, gender, birthdate, cohabDate, dateOfMarriage, relationship, email, spouseCoverage, employer, insuranceCarrier, dependentCertNumber } = dependent;

            const firstnameError = await Services.validateInput('firstname', firstname);
            const lastnameError = await Services.validateInput('lastname', lastname);
            const genderError = await Services.validateInput('gender', gender);
            const birthdateError = await Services.validateInput('birthdate', birthdate, i18n.locale);
            const cohabDateError = localizationMaps.toEnglish("relationships", relationship)?.toUpperCase() === 'COMMON LAW' ? await Services.validateInput('birthdate', cohabDate, i18n.locale) : false;
            const dateOfMarriageError = localizationMaps.toEnglish("relationships", relationship)?.toUpperCase() === 'SPOUSE' ? await Services.validateInput('birthdate', dateOfMarriage, i18n.locale) : false;
            const relationshipError = await Services.validateInput('relationship', relationship);
            const emailError = email.length === 0 ? false : await Services.validateInput('email', email);
            let spouseError = false;

            if (localizationMaps.toEnglish("relationships", relationship)?.toUpperCase() === 'SPOUSE' || localizationMaps.toEnglish("relationships", relationship)?.toUpperCase() === 'COMMON LAW') {
                if (seenSpouse) {
                    spouseError = i18n.t(tokens.labels.signup.youCanOnlyHave1Spouse);
                } else {
                    seenSpouse = true;
                }
            }

            let employerError = false;
            let insuranceCarrierError = false;
            let dependentCertNumberError = false;

            if (spouseCoverage === true) {
                employerError = await Services.validateInput('checkEmpty', employer || '');
                insuranceCarrierError = await Services.validateInput('checkEmpty', insuranceCarrier || '');
                dependentCertNumberError = await Services.validateInput('checkEmpty', dependentCertNumber || '');
            }

            this.setState({
                [`firstnameError${ i }`]: firstnameError,
                [`lastnameError${ i }`]: lastnameError,
                [`genderError${ i }`]: genderError,
                [`birthdateError${ i }`]: birthdateError,
                [`cohabDateError${ i }`]: cohabDateError,
                [`dateOfMarriageError${ i }`]: dateOfMarriageError,
                [`relationshipError${ i }`]: relationshipError,
                [`emailError${ i }`]: emailError,
                [`employerError${ i }`]: employerError,
                [`insuranceCarrierError${ i }`]: insuranceCarrierError,
                [`dependentCertNumberError${ i }`]: dependentCertNumberError,
                [`spouseError${ i }`]: spouseError
            });

            if (
                firstnameError ||
                lastnameError ||
                genderError ||
                birthdateError ||
                cohabDateError ||
                dateOfMarriageError ||
                relationshipError ||
                emailError ||
                spouseError ||
                employerError ||
                insuranceCarrierError ||
                dependentCertNumberError
            ) {
                isValid = false;
                return false;
            }

            return true;
        });

        await Promise.all(promises);

        return isValid;
    };

    /**
     * @private
     */
    _ageValidation = async () => {
        let doCheck = false;

        const promises = this.state.dependents.map((dependent, i) => {
            const { birthdate } = dependent;
            const relationship = localizationMaps.toEnglish("relationships", dependent.relationship);

            // Calculate age in years
            const age = moment().utc()
                .diff(birthdate, 'years');

            if (age > 25 && (relationship.toLowerCase() === 'child' || relationship.toLowerCase() === 'grandchild')) {
                this.setState({
                    [`birthdateError${ i }`]: i18n.t(tokens.labels.validations.childDependentsUnder26)
                });

                return null;
            }

            if (relationship.toLowerCase() === 'child' || relationship.toLowerCase() === 'grandchild') {
                if (age > 20 && age < 26) {
                    doCheck = true;
                    return false;
                }
            }

            return true;
        });

        await Promise.all(promises);

        if (doCheck) {
            this._toggleDependentDialog(true);
            return false;
        }

        return true;
    };

    _createDependentTemplates = (numberOfDependents) => {
        const dependents = [];

        for (let i = 0; i < numberOfDependents; i++) {
            const dependent = {
                firstname: '',
                lastname: '',
                gender: '',
                birthdate: '',
                cohabDate: '',
                relationship: '',
                email: '',
                employer: null,
                insuranceCarrier: null,
                dependentCertNumber: null,
                spouseCoverage: null,
                primaryBenefits: [],
                dateOfMarriage: ''
            };

            dependents.push(dependent);
            this.props.dispatch(addDependentToList(dependent));
        }

        this.setState({
            dependents,
            hasNoDependents: false
        });
    };

    _setStudentStatus = async () => {
        await this.setState({
            dependents: this.state.dependents.map((dependent) => {
                const { birthdate, relationship } = dependent;
                const age = moment().utc().diff(birthdate, 'years');

                if (relationship.toLowerCase() === 'child' || relationship.toLowerCase() === 'grandchild') {
                    if (age > 20 && age < 26) {
                        return {
                            ...dependent,
                            isStudent: 1
                        };
                    }
                }

                return dependent;
            })
        });

        this._next(true, true);
    };

    _next = async (skipValidation = false, skipNoDependentsCheck = false) => {
        if (skipValidation || (this.state.hasNoDependents && this.state.numberOfDependents === 0)) {
            if (!skipNoDependentsCheck) {
                this._toggleNoDependentsMessage();
            } else {
                // mark the saved state to true, we have hit next and everything is valid
                this.props.setSavedState('dependents', true);
                this.props.stepCompletion([ ...this.state.dependents ], 'dependents', false);
            }

            return;
        }

        if (this.state.hasNoDependents && this.state.numberOfDependents !== 0) {
            await this._createDependentTemplates(this.state.numberOfDependents);
            return;
        }

        const isValid = await this._validateForm();

        if (!isValid) {
            return;
        }

        // check that all dependents are eligible and set any that are not to be reminded
        await this._checkDependentEligibility();

        // if there are ineligible dependents, make sure the member has checked off on it before continuing
        if (this.state.ineligibleDependents.length > 0 && !this.state.hasAcceptedInEligibleDependentTerms) {
            return;
        }

        const ageValidated = await this._ageValidation();

        if (ageValidated) {
            // mark the saved state to true, we have hit next and everything is valid
            this.props.setSavedState('dependents', true);

            this.props.stepCompletion([ ...this.state.dependents ], 'dependents', false);
        }
    };

    _toggleNoDependentsMessage = () => {
        // this.state.numberOfDependents
        this.setState({
            showNoDependentsMessage: !this.state.showNoDependentsMessage
        });
    };

    _removeDependent = async (index) => {
        await this.setState({
            dependents: this.state.dependents.filter((dependent, i) => index !== i)
        });

        this.props.dispatch(removeDependentFromList(index));

        if (this.state.dependents.length === 0) {
            this.setState({
                numberOfDependents: 0,
                hasNoDependents: true
            });
        }
    };

    _addDependent = () => {
        const dependent = {
            firstname: '',
            lastname: '',
            gender: '',
            birthdate: '',
            cohabDate: '',
            relationship: '',
            email: '',
            employer: null,
            insuranceCarrier: null,
            dependentCertNumber: null,
            spouseCoverage: null,
            primaryBenefits: [],
            dateOfMarriage: ''
        };

        this.setState({
            dependents: [ ...this.state.dependents, dependent ]
        });

        this.props.dispatch(addDependentToList(dependent));
    };

    _removeDependentLabel = (i, showDeleteButton = true) => (
        <div style={{ display: 'flex', flexDirection: 'row', flex: 1, alignItems: 'center', margin: "20px 0 0px 0" }}>
            <Text medium bold>{ `${ i18n.t(tokens.labels.signup.dependent) } ${ i + 1 }` }</Text>

            {
                showDeleteButton &&
                    <Button
                        onClick={ () => this._removeDependent(i) }
                        transparent
                        variant="outline"
                        customStyle={{
                            width: 30,
                            minWidth: 30,
                            padding: 0,
                            "&:hover": {
                                // TODO: refactor this style to the ui library if text variants are used more frequently in control site
                                // makes the hover color lighter for this button
                                backgroundColor: "#f5f5f5"
                            }
                        }}
                    >
                        <RemoveIcon style={{ fontSize: 17, lineHeight: '14px', color: 'inherit' }} />
                    </Button>
            }
        </div>
    );

    render() {
        const styles = {
            form: {
                display: 'flex',
                flexDirection: 'column'
            },
            stepperBackBtn: {
                margin: '0px auto 0px 0px !important'
            },
            stepperBtnContainer: globalStyles.stepperBtnContainer,
            stepperForm: globalStyles.stepperForm,
            stepperNextBtn: globalStyles.stepperNextBtn,
            bubbleListSelector: {
                margin: '10px 0',
                display: 'flex',
                flexWrap: 'wrap',
                gap: '10px'
            },
            bubble: {
                fontSize: '12px',
                padding: '4px 13px',
                minWidth: '0',
                margin: '0'
            }
        };

        const coverageTypeLocked = this.props.userDetails.blockEnrolmentCoverageTypeChanges === 1;
        const coverageType = localizationMaps.toEnglish("coverageTypes", this.props.userDetails.coverageType).toUpperCase();

        if (coverageTypeLocked && coverageType === 'SOLO') {
            return (
                <div>
                    <Text largest bold margin="0 0 10px 0">{ i18n.t(tokens.titles.dependents) }</Text>

                    <div style={styles.form}>
                        <Text small>
                            { i18n.t(tokens.labels.signup.soloCoverageTypeMessage) }
                        </Text>

                        <div style={styles.stepperBtnContainer}>
                            <Button
                                style={styles.stepperNextBtn}
                                onClick={() => this._next(true, true)}
                                color="primary"
                                fullWidth
                            >
                                { i18n.t(tokens.buttons.continue) }
                            </Button>
                        </div>
                    </div>
                </div>
            );
        }

        return (
            <div>
                <Text largest bold margin="0 0 10px 0">{ coverageTypeLocked && coverageType === "DUO" ? i18n.t(tokens.labels.signup.dependent) : i18n.t(tokens.titles.dependents) }</Text>

                <div style={styles.form}>
                    <Tip
                        left
                        show={ this.props.tips.dependentBreakdown }
                        close={ () => this.props.hideTip('dependentBreakdown') }
                        customStyle={{ marginTop: -100 }}
                    >
                        { i18n.t(tokens.labels.signup.addingDependentsToHealthPlan) }
                        <br /><br />
                        { i18n.t(tokens.labels.signup.validDependentsInclude) } <span style={{ fontWeight: 'bold' }}>{ i18n.t(tokens.labels.signup.spouse)}</span> ({ i18n.t(tokens.labels.signup.includingSeparated) }),&nbsp;
                        <span style={{ fontWeight: 'bold' }}>{ i18n.t(tokens.labels.signup.commonLawPartners) }</span> ({ i18n.t(tokens.labels.signup.livingTogetherInConjugalRelationshipForMinimumOf1Year) }),&nbsp;
                        { i18n.t(tokens.labels.signup.and) } <span style={{ fontWeight: 'bold' }}>{ i18n.t(tokens.labels.signup.children) }</span> ({ i18n.t(tokens.labels.signup.adoptedOrNatural) }).
                    </Tip>

                    {
                        this.state.dependents.length === 0 ?
                            <TextInput
                                label={ i18n.t(tokens.labels.signup.howManyDependentsDoYouHave) }
                                value={ this.state.numberOfDependents }
                                onChange={(value) => this._handleInputChange('numberOfDependents', value)}
                                fullWidth
                            />
                            :
                            <div>

                                {
                                    !this.props.isMarketplaceOnly &&
                                        <Tip
                                            right
                                            show={ this.props.tips.dependentEmail }
                                            close={ () => this.props.hideTip('dependentEmail') }
                                            customStyle={{ marginTop: 160 }}
                                        >
                                            { i18n.t(tokens.labels.signup.optionallyProvideAnEmailAddressForYourDependent) }&nbsp;
                                            <span style={{ fontWeight: 'bold' }}>{ i18n.t(tokens.labels.signup.theirOwnLogin) }</span>,&nbsp;
                                            { i18n.t(tokens.labels.signup.viewCoverageSubmitClaimsAndMore) }
                                        </Tip>
                                }

                                {
                                    this.state.dependents.map((dependent, i) => (
                                        <>
                                            {
                                                !coverageTypeLocked &&
                                                    this._removeDependentLabel(i, true)
                                            }

                                            {
                                                (coverageTypeLocked && coverageType === "FAMILY") &&
                                                    this._removeDependentLabel(i, i > 1)
                                            }

                                            <TextInput
                                                label={ i18n.t(tokens.labels.firstname) }
                                                autoComplete="name"
                                                value={ dependent.firstname }
                                                onBlur={() => this._onBlur('firstname', i)}
                                                onChange={(value) => this._handleInputChange('firstname', value, i)}
                                                error={Boolean(this.state[`firstnameError${ i }`])}
                                                helperText={this.state[`firstnameError${ i }`]}
                                                fullWidth
                                            />

                                            <TextInput
                                                label={ i18n.t(tokens.labels.lastname) }
                                                value={dependent.lastname}
                                                onBlur={() => this._onBlur('lastname', i)}
                                                onChange={(value) => this._handleInputChange('lastname', value, i)}
                                                error={Boolean(this.state[`lastnameError${ i }`])}
                                                helperText={this.state[`lastnameError${ i }`]}
                                                fullWidth
                                            />

                                            {
                                                !this.props.isMarketplaceOnly &&
                                                    <TextInput
                                                        label={ i18n.t(tokens.labels.account.emailOptional) }
                                                        value={dependent.email}
                                                        onBlur={() => this._onBlur('email', i)}
                                                        onChange={(value) => this._handleInputChange('email', value, i)}
                                                        error={Boolean(this.state[`emailError${ i }`])}
                                                        helperText={this.state[`emailError${ i }`]}
                                                        fullWidth
                                                    />
                                            }

                                            <TextInput
                                                label={ i18n.t(tokens.labels.gender) }
                                                value={dependent.gender}
                                                fullWidth
                                                onBlur={() => this._onBlur('gender', i)}
                                                onChange={(value) => this._handleInputChange('gender', value, i)}
                                                error={Boolean(this.state[`genderError${ i }`])}
                                                helperText={this.state[`genderError${ i }`]}
                                                select
                                                selectOptions={ Services.selectFieldOptions.genderOptions() }
                                            />

                                            <DateInput
                                                label={ i18n.t(tokens.labels.account.dateOfBirthYYYYMMDD) }
                                                value={dependent.birthdate}
                                                onBlur={() => this._onBlur('birthdate', i)}
                                                onChange={(value) => this._handleInputChange('birthdate', value, i)}
                                                error={Boolean(this.state[`birthdateError${ i }`])}
                                                helperText={ this.state[`birthdateError${ i }`] }
                                                fullWidth
                                            />

                                            <TextInput
                                                label={ i18n.t(tokens.labels.relationship) }
                                                value={ dependent.relationship }
                                                fullWidth
                                                onBlur={() => this._onBlur('relationship', i)}
                                                onChange={(value) => this._handleInputChange('relationship', value, i)}
                                                error={Boolean(this.state[`relationshipError${ i }`]) || Boolean(this.state[`spouseError${ i }`])}
                                                helperText={this.state[`relationshipError${ i }`] || this.state[`spouseError${ i }`] }
                                                select
                                                selectOptions={this.state.options}
                                            />

                                            {
                                                !this.props.isMarketplaceOnly &&
                                                <>
                                                    {
                                                        localizationMaps.toEnglish("relationships", dependent.relationship).toLowerCase() === "spouse" ?
                                                            <DateInput
                                                                label={ i18n.t(tokens.labels.dependents.marriageDate) }
                                                                value={ dependent.dateOfMarriage }
                                                                onBlur={() => this._onBlur('dateOfMarriage', i)}
                                                                onChange={(value) => this._handleInputChange('dateOfMarriage', value, i)}
                                                                error={Boolean(this.state[`dateOfMarriageError${ i }`])}
                                                                helperText={this.state[`dateOfMarriageError${ i }`] || this.state[`dateOfMarriageMsg${ i }`]}
                                                                required
                                                                fullWidth
                                                            />
                                                            :
                                                            null
                                                    }
                                                    {
                                                        localizationMaps.toEnglish("maritalStatuses", dependent.relationship) === 'Common Law' &&
                                                        <DateInput
                                                            label={ i18n.t(tokens.labels.signup.coHabDateYyyyMmDd) }
                                                            value={dependent.cohabDate}
                                                            onBlur={() => this._onBlur('cohabDate', i)}
                                                            onChange={(value) => this._handleInputChange('cohabDate', value, i)}
                                                            error={Boolean(this.state[`cohabDateError${ i }`])}
                                                            helperText={this.state[`cohabDateError${ i }`] || this.state[`cohabDateMsg${ i }`]}
                                                            required
                                                            fullWidth
                                                        />
                                                    }

                                                    <Text
                                                        medium
                                                        light
                                                        margin="10px 0 10px 0"
                                                    >
                                                        { i18n.t(tokens.labels.dependents.doTheyHaveProvincialHealthCoverage) }
                                                    </Text>
                                                                
                                                    <BubbleSelectBlock
                                                        first={{
                                                            title: i18n.t(tokens.buttons.yes),
                                                            selected: dependent.hasProvincialHealthCoverage === true,
                                                            field: 'hasProvincialHealthCoverageYes'
                                                        }}
                                                        second={{
                                                            title: i18n.t(tokens.buttons.no),
                                                            selected: !dependent.hasProvincialHealthCoverage,
                                                            field: 'hasProvincialHealthCoverageNo'
                                                        }}
                                                        onBlur={() => this._onBlur('provincialHealthCoverage', i)}
                                                        handleChange={ async (field) => {
                                                            if (field === 'hasProvincialHealthCoverageYes') {
                                                                await this._handleInputChange('hasProvincialHealthCoverage', true, i, true);
                                                            } else {
                                                                await this._handleInputChange('hasProvincialHealthCoverage', false, i, true);
                                                            }
                                                        }}
                                                    />

                                                    {
                                                        (localizationMaps.toEnglish("relationships", dependent.relationship) === 'Spouse' ||
                                                            localizationMaps.toEnglish("relationships", dependent.relationship) === 'Common Law') &&
                                                        <div>
                                                            <FormControl component="fieldset">
                                                                <Text
                                                                    medium
                                                                    light
                                                                    margin="10px 0 10px 0"
                                                                >
                                                                    { i18n.t(tokens.labels.changeRequests.doesThisDependentHaveComparableCoverage) }
                                                                </Text>

                                                                <BubbleSelectBlock
                                                                    first={{
                                                                        title: i18n.t(tokens.buttons.yes),
                                                                        selected: dependent.spouseCoverage === true,
                                                                        field: 'spouseCoverageYes'
                                                                    }}
                                                                    second={{
                                                                        title: i18n.t(tokens.buttons.no),
                                                                        selected: !dependent.spouseCoverage,
                                                                        field: 'spouseCoverageNo'
                                                                    }}
                                                                    onBlur={() => this._onBlur('spouseCoverage', i)}
                                                                    handleChange={ async (field) => {
                                                                        if (field === 'spouseCoverageYes') {
                                                                            await this._changeSpouseCoverage(true, i);
                                                                        } else {
                                                                            await this._changeSpouseCoverage(false, i);
                                                                        }
                                                                    }}
                                                                />
                                                            </FormControl>

                                                            {
                                                                dependent.spouseCoverage &&
                                                                <React.Fragment>
                                                                    <TextInput
                                                                        label={ i18n.t(tokens.labels.dependents.dependentOrganization) }
                                                                        value={dependent.employer}
                                                                        fullWidth
                                                                        onBlur={() => this._onBlur('employer', i)}
                                                                        onChange={(value) => this._handleInputChange('employer', value, i)}
                                                                        error={Boolean(this.state[`employerError${ i }`])}
                                                                        helperText={this.state[`employerError${ i }`] }
                                                                    />

                                                                    <TextInput
                                                                        label={ i18n.t(tokens.labels.dependents.dependentCurrentInsuranceCarrier) }
                                                                        value={dependent.insuranceCarrier}
                                                                        fullWidth
                                                                        onBlur={() => this._onBlur('insuranceCarrier', i)}
                                                                        onChange={(value) => this._handleInputChange('insuranceCarrier', value, i)}
                                                                        error={Boolean(this.state[`insuranceCarrierError${ i }`])}
                                                                        helperText={this.state[`insuranceCarrierError${ i }`]}
                                                                        select={
                                                                            (!dependent.insuranceCarrier || insuranceOptions.indexOf(dependent.insuranceCarrier) > -1)
                                                                            && dependent.insuranceCarrier !== ''
                                                                        }
                                                                        selectOptions={insuranceOptions}
                                                                    />

                                                                    <TextInput
                                                                        label={ i18n.t(tokens.labels.dependents.dependentCertificateNumber) }
                                                                        value={dependent.dependentCertNumber}
                                                                        fullWidth
                                                                        onBlur={() => this._onBlur('dependentCertNumber', i)}
                                                                        onChange={(value) => this._handleInputChange('dependentCertNumber', value, i)}
                                                                        error={Boolean(this.state[`dependentCertNumberError${ i }`])}
                                                                        helperText={this.state[`dependentCertNumberError${ i }`] }
                                                                    />
                                                                    <div style={ styles.bubbleListSelector }>
                                                                        {
                                                                            primaryBenefitsNames().map((option) => (
                                                                                <Bubble
                                                                                    customStyle={ styles.bubble }
                                                                                    key={ option }
                                                                                    label={ option }
                                                                                    isSelected={ dependent.primaryBenefits?.includes(option) }
                                                                                    onClick={() => this._handleBubbleListSelector(option, i)}
                                                                                />
                                                                            ))
                                                                        }
                                                                    </div>
                                                                </React.Fragment>
                                                            }
                                                        </div>
                                                    }
                                                </>
                                            }
                                        </>
                                    ))
                                }
                                {
                                    (coverageTypeLocked && coverageType !== 'FAMILY') ?
                                        null
                                        :
                                        <>
                                            <Tip
                                                left
                                                show={ this.props.tips.addAnotherDependent }
                                                close={ () => this.props.hideTip('addAnotherDependent') }
                                                customStyle={{ marginTop: -50 }}
                                            >
                                                { i18n.t(tokens.labels.signup.addAnotherDependentToHealthPlan) }&nbsp;
                                                <span style={{ fontWeight: 'bold' }}>"{ i18n.t(tokens.titles["add dependent"])}"</span> { i18n.t(tokens.labels.signup.button) }.
                                            </Tip>

                                            <Button
                                                onClick={ this._addDependent }
                                                transparent
                                                variant="outline"
                                                customStyle={{
                                                    marginRight: 'auto',
                                                    margin: "20px auto 0 0",
                                                    "&:hover": {
                                                        // TODO: refactor this style to the ui library if text variants are used more frequently in control site
                                                        // makes the hover color lighter for this button
                                                        backgroundColor: "#f5f5f5"
                                                    }
                                                }}
                                            >
                                                <AddIcon style={{ fontSize: 17, lineHeight: '14px', color: 'inherit', marginRight: 5 }} />
                                                { i18n.t(tokens.titles["add dependent"]) }
                                            </Button>
                                        </>
                                }
                            </div>
                    }

                    {
                        this.state.ineligibleDependents.length > 0 &&
                            <React.Fragment>
                                <Text medium display="inline-block" margin="20px 0 0 0" >
                                    { i18n.t(tokens.labels.signup.belowDependentsNotYetEligibleForBenefits) }
                                </Text>

                                {
                                    this.state.ineligibleDependents.map((dependent) => (
                                        <Text medium bold margin="20px 0 0 0">
                                            { `${ dependent.firstname } ${ dependent.lastname } - Eligible on ${ dates.getStartOfDay(this._getEligibilityDate(dependent.cohabDate), 'll') }` }
                                        </Text>
                                    ))
                                }

                                <FormGroup row style={{ marginTop: 20 }}>
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={ this.state.hasAcceptedInEligibleDependentTerms }
                                                onChange={ () => this.setState({ hasAcceptedInEligibleDependentTerms: !this.state.hasAcceptedInEligibleDependentTerms }) }
                                                value="hasAcceptedInEligibleDependentTerms"
                                                color="secondary"
                                            />
                                        }
                                        label={ i18n.t(tokens.labels.signup.acknowledgeFullyUnderstandAboveConditions) }
                                    />
                                </FormGroup>
                            </React.Fragment>
                    }

                    <div style={styles.stepperBtnContainer}>
                        <Button
                            style={styles.stepperNextBtn}
                            onClick={() => this._next(false)}
                            color="primary"
                        >
                            { i18n.t(tokens.buttons.continue) }
                        </Button>
                    </div>
                </div>

                {/* Dependent Dialog */}
                <Modal
                    open={this.state.showDependentDialog}
                    onClose={() => this._toggleDependentDialog(false)}
                    primaryButton={{
                        onClick: () => this._setStudentStatus()
                    }}
                >
                    <Text medium display="inline-block" >
                        { i18n.t(tokens.labels.signup.confirmDependentStatus) }
                    </Text>
                </Modal>

                <Modal
                    open={this.state.showNoDependentsMessage}
                    onClose={() => this._toggleNoDependentsMessage()}
                    title={ i18n.t(tokens.titles.areYouSure) }
                    primaryButton={{
                        label: i18n.t(tokens.buttons.continue),
                        onClick: () => this._next(true, true)
                    }}
                    loader={this.props.loader}
                >
                    <Text medium display="inline-block" >
                        { i18n.t(tokens.labels.signup.continueWithNoDependents) }
                    </Text>
                </Modal>
            </div>
        );
    }
}

Dependents.propTypes = {
    dependents: PropTypes.array
};

/**
 *
 * @param {*} state
 */
const mapStateToProps = (state) => ({
    dependents: state.signup.dependents,
    userDetails: state.signup.userDetails
});

export default (connect(mapStateToProps)(Dependents));